<template>
    <hox-modal @close="close">
        <hox-loading-layer v-if="isSettingMetadataValues" />
        <template #header>Set "{{ metadataField.name }}" values</template>

        <form @submit.prevent="setMetadataValue">
            <hox-input v-model="metadataValue">
                <template #label>Column value</template>
            </hox-input>
        </form>
        <hox-alert size="small">
            <template #content>
                <p v-if="hasSelectedDeliverables">
                    This value will be set on the column "{{ metadataField.name }}" for
                    {{ deliverablesToSetMetadataValueOnCount }} selected line {{ itemOrItems }} on
                    <template v-if="allSelectedMode">all the pages.</template>
                    <template v-else>the current page.</template>
                </p>
                <template v-else>
                    <p>
                        This value will be set on the column "{{ metadataField.name }}" for all line items on the
                        current page.
                    </p>
                    <p>
                        You can select specific rows if you wish to limit the line items that this value is applied to.
                    </p>
                </template>
            </template>
        </hox-alert>
        <Alert v-if="hasErrorSettingMetadataValues" banner type="error">
            <template #desc>
                There was an unexpected error and no changes were saved. Hopefully it was a temporary issue and should
                work if you try again in a few moments.
            </template>
        </Alert>
        <CampaignLockedNotice :no-polling="true" />
        <template #footer>
            <Button type="primary" :disabled="isCampaignLocked" @click="setMetadataValue">Save</Button>
        </template>
    </hox-modal>
</template>

<script lang="ts">
import getComputedDeliverableIdentifiers from "@/services/deliverables/getComputedDeliverableIdentifiers";
import type { PropType } from "vue";

import {
    addComputedDeliverablesMetadataValue,
    addComputedDeliverablesMetadataValueUsingFilter,
    removeComputedDeliverablesMetadataValueByFieldId,
    removeComputedDeliverablesMetadataValueUsingFilter
} from "@/services/Metadata";

import CampaignLockedNotice from "@/views/Campaign/CampaignLockedNotice.vue";

import { NotificationsAction } from "@/store/modules/notifications";
import { JobsAction, type JobNotification } from "@/store/modules/jobs";
import { JobStatusToNotificationStatus } from "@/enums/jobs";
import { NotificationTypes } from "@/enums/notifications";
import type { ConstructedDeliverable } from "@/types/types";
import { useCreativeLibraryFiltersStore } from "@/store/pinia/creativeLibraryFilters";
import { mapStores } from "pinia";

export default {
    components: { CampaignLockedNotice },
    props: {
        allSelectedMode: {
            type: Boolean,
            default: false
        },

        deliverables: {
            required: true,
            type: Array as PropType<ConstructedDeliverable[]>
        },

        metadataFieldId: {
            required: true,
            type: String
        },

        selectedDeliverables: {
            required: true,
            type: Array as PropType<ConstructedDeliverable[]>
        },

        totalItems: {
            required: true,
            type: Number
        }
    },

    emits: ["metadataValueUpdatedOnDeliverables", "close"],

    data() {
        return {
            hasErrorSettingMetadataValues: false,
            isSettingMetadataValues: false,
            metadataValue: ""
        };
    },

    computed: {
        ...mapStores(useCreativeLibraryFiltersStore),
        deliverablesToSetMetadataValueOn() {
            return this.hasSelectedDeliverables ? this.selectedDeliverables : this.deliverables;
        },

        deliverablesToSetMetadataValueOnCount() {
            if (this.allSelectedMode) {
                return this.totalItems;
            }

            return this.deliverablesToSetMetadataValueOn.length;
        },

        hasSelectedDeliverables() {
            return this.selectedDeliverables.length > 0 || this.allSelectedMode;
        },

        isCampaignLocked() {
            return this.$store.state.campaign.isLocked;
        },

        itemOrItems() {
            return this.deliverablesToSetMetadataValueOnCount === 1 ? "item" : "items";
        },

        metadataField() {
            return this.$store.state.deliverableLibrary.metadataFieldsByFieldId[this.metadataFieldId];
        }
    },

    async mounted() {
        await this.$nextTick();
    },

    methods: {
        close() {
            this.$emit("close");
        },

        setMetadataValue() {
            if (this.allSelectedMode) {
                this.setMetadataValueUsingFilter();
                return;
            }

            this.setMetadataValueOnDeliverables();
        },

        async setMetadataValueUsingFilter() {
            this.isSettingMetadataValues = true;
            this.hasErrorSettingMetadataValues = false;

            try {
                if (this.metadataValue) {
                    const job = (await addComputedDeliverablesMetadataValueUsingFilter(
                        this.creativeLibraryFiltersStore.getConstructedLibraryFilters,
                        this.metadataFieldId,
                        this.metadataValue
                    )) as JobNotification;

                    const notificationId = await this.$store.dispatch(NotificationsAction.Add, {
                        message: job.message,
                        status: JobStatusToNotificationStatus[job.status],
                        type: NotificationTypes.BackgroundUpdate,
                        job
                    });

                    this.$store.dispatch(JobsAction.SetNotificationIdByActiveJobId, {
                        jobId: job._id,
                        notificationId
                    });

                    this.$snackbar.info(
                        "Your task is now being processed. Some features in this campaign will be disabled until this is completed."
                    );
                } else {
                    const job = (await removeComputedDeliverablesMetadataValueUsingFilter(
                        this.creativeLibraryFiltersStore.getConstructedLibraryFilters,
                        this.metadataFieldId
                    )) as JobNotification;

                    const notificationId = await this.$store.dispatch(NotificationsAction.Add, {
                        message: job.message,
                        status: JobStatusToNotificationStatus[job.status],
                        type: NotificationTypes.BackgroundUpdate,
                        job
                    });

                    this.$store.dispatch(JobsAction.SetNotificationIdByActiveJobId, {
                        jobId: job._id,
                        notificationId
                    });

                    this.$snackbar.info(
                        "Your task is now being processed. Some features in this campaign will be disabled until this is completed."
                    );
                }
            } catch {
                this.hasErrorSettingMetadataValues = true;
                return;
            } finally {
                this.isSettingMetadataValues = false;
            }
            this.close();
        },

        async setMetadataValueOnDeliverables() {
            this.isSettingMetadataValues = true;
            this.hasErrorSettingMetadataValues = false;
            const deliverableIdentifiers = getComputedDeliverableIdentifiers(this.deliverablesToSetMetadataValueOn);
            const idHashes = this.deliverablesToSetMetadataValueOn.map(({ idHash }) => idHash);
            try {
                if (this.metadataValue) {
                    const metadataValue = await addComputedDeliverablesMetadataValue(
                        deliverableIdentifiers,
                        this.metadataFieldId,
                        this.metadataValue
                    );
                    this.$emit("metadataValueUpdatedOnDeliverables", { idHashes, metadataValue });
                } else {
                    await removeComputedDeliverablesMetadataValueByFieldId(
                        deliverableIdentifiers,
                        this.metadataFieldId
                    );
                    this.$emit("metadataValueUpdatedOnDeliverables", {
                        idHashes,
                        metadataValue: {
                            field: {
                                _id: this.metadataFieldId
                            },
                            value: undefined
                        }
                    });
                }
            } catch {
                this.hasErrorSettingMetadataValues = true;
                return;
            } finally {
                this.isSettingMetadataValues = false;
            }
            this.close();
        }
    }
};
</script>

<style lang="scss">
@use "@style/variables" as *;
</style>
